import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../components/UI/layout/Layout";
import { SmallButton } from "../components/UI/smallButton/SmallButton";
import { useNavigate } from "react-router-dom";
import TournamentBracket from "../components/league/TournamentBracket";
import { media } from "../styles/GlobalStyles";
import CONFIG from "../config";
import socket from "../socket";

const SERVER_API_URL = CONFIG.API_URL.replace(/\/api$/, "");

const Container = styled.div`
  width: 100%;
  max-width: 120rem;
  margin: 0 auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${media.phone} {
    flex-direction: column;
    gap: 2rem;
  }
`;

const Title = styled.h1`
  font-size: 4.8rem;
  color: var(--color-white);
  text-shadow: 4px 4px 0 var(--color-black);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 32rem;
  gap: 3rem;

  ${media.desktopS} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--color-white);
  border: 3px solid var(--color-black);
  box-shadow: 0 1rem 0 var(--color-black);
  border-radius: 2rem;
  padding: 2.4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.4rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1.2rem;
`;

const Feed = styled.div`
  height: 60rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding-right: 1rem;
`;

const Message = styled(motion.div)<{ $tone?: "info" | "success" | "warn" }>`
  padding: 1.2rem;
  background: ${({ $tone }) =>
    $tone === "success" ? "#dcfce7" : $tone === "warn" ? "#fef3c7" : "#f3f4f6"};
  border-radius: 1.2rem;
  font-size: 1.4rem;
  border: 2px solid transparent;
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  transition: border-color 0.2s, transform 0.1s;

  &:hover {
    border-color: ${({ onClick }) =>
      onClick ? "var(--color-purple)" : "transparent"};
    transform: ${({ onClick }) => (onClick ? "translateY(-1px)" : "none")};
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 0.4rem;
  font-weight: bold;
`;

const RoundBanner = styled(motion.div)`
  background: var(--color-purple);
  color: var(--color-white);
  padding: 1.6rem;
  border-radius: 1.2rem;
  text-align: center;
  font-size: 1.8rem;
  font-weight: bold;
  text-shadow: 2px 2px 0 var(--color-black);
  border: 3px solid var(--color-black);
`;

type FeedItem =
  | {
      kind: "live";
      id: string; // roomId
      seriesId: string;
      p1: string;
      p2: string;
      ts: number;
    }
  | {
      kind: "result";
      id: string;
      seriesId: string;
      winner: string;
      loser: string;
      score1: number;
      score2: number;
      ts: number;
    }
  | {
      kind: "round";
      id: string;
      round: number;
      ts: number;
    };

const LeagueDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [league, setLeague] = useState<any>(null);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [roundBanner, setRoundBanner] = useState<number | null>(null);
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── REST polling (5s) for bracket + leaderboard (fallback for non-socket state)
  useEffect(() => {
    const fetchLeague = async () => {
      try {
        const resp = await fetch(`${SERVER_API_URL}/api/league/active`);
        const data = await resp.json();
        setLeague(data);
      } catch (err) {
        /* ignore transient */
      }
    };

    const fetchLeaderboard = async () => {
      try {
        const resp = await fetch(`${SERVER_API_URL}/api/league/leaderboard`);
        const data = await resp.json();
        setLeaderboard(data);
      } catch (err) {
        /* ignore */
      }
    };

    fetchLeague();
    fetchLeaderboard();
    const inv = setInterval(fetchLeague, 5000);
    const invLb = setInterval(fetchLeaderboard, 15000);
    return () => {
      clearInterval(inv);
      clearInterval(invLb);
    };
  }, []);

  // ── Socket subscriptions for live match activity
  useEffect(() => {
    if (!socket.connected) socket.connect();

    const onGameStarted = (data: any) => {
      // Emitted by tournamentService when a new league room is created.
      const item: FeedItem = {
        kind: "live",
        id: data.roomId,
        seriesId: data.seriesId,
        p1: data.p1Name ?? "P1",
        p2: data.p2Name ?? "P2",
        ts: Date.now(),
      };
      setFeed((f) => [item, ...f].slice(0, 30));
    };

    const onGamePrompt = (data: any) => {
      // Targeted at agents, but server also broadcasts an informational copy.
      // We treat this identically to league-game-started for the dashboard feed.
      if (!data?.roomId) return;
      const item: FeedItem = {
        kind: "live",
        id: data.roomId,
        seriesId: data.seriesId ?? data.roomId.split("-G")[0].slice("LEAGUE-".length),
        p1: data.p1Name ?? "P1",
        p2: data.p2Name ?? "P2",
        ts: Date.now(),
      };
      setFeed((f) => {
        if (f.some((x) => x.kind === "live" && x.id === item.id)) return f;
        return [item, ...f].slice(0, 30);
      });
    };

    const onSeriesCompleted = (data: any) => {
      const item: FeedItem = {
        kind: "result",
        id: `series-${data.seriesId}-${Date.now()}`,
        seriesId: data.seriesId,
        winner: data.winnerName ?? "Winner",
        loser: data.loserName ?? "Opponent",
        score1: data.score1 ?? 0,
        score2: data.score2 ?? 0,
        ts: Date.now(),
      };
      setFeed((f) => [item, ...f].slice(0, 30));
    };

    const onRoundAdvanced = (data: any) => {
      setRoundBanner(data.round);
      if (bannerTimer.current) clearTimeout(bannerTimer.current);
      bannerTimer.current = setTimeout(() => setRoundBanner(null), 4000);
      setFeed((f) =>
        [
          {
            kind: "round" as const,
            id: `round-${data.round}-${Date.now()}`,
            round: data.round,
            ts: Date.now(),
          },
          ...f,
        ].slice(0, 30),
      );
    };

    const onLeagueCompleted = (_data: any) => {
      // Trigger a fresh pull of league state on completion.
      fetch(`${SERVER_API_URL}/api/league/active`)
        .then((r) => r.json())
        .then(setLeague)
        .catch(() => {});
    };

    socket.on("league-game-started", onGameStarted);
    socket.on("league-game-prompt", onGamePrompt);
    socket.on("league-series-completed", onSeriesCompleted);
    socket.on("league-round-advanced", onRoundAdvanced);
    socket.on("league-completed", onLeagueCompleted);

    return () => {
      socket.off("league-game-started", onGameStarted);
      socket.off("league-game-prompt", onGamePrompt);
      socket.off("league-series-completed", onSeriesCompleted);
      socket.off("league-round-advanced", onRoundAdvanced);
      socket.off("league-completed", onLeagueCompleted);
      if (bannerTimer.current) clearTimeout(bannerTimer.current);
      // Do NOT disconnect — other pages may share the socket.
    };
  }, []);

  const openMatch = (seriesId: string) => {
    navigate(`/league/match/${seriesId}`);
  };

  const registerSnippet = 'npx openleague register "MyBot" && npx openleague play';

  return (
    <Layout color="purple">
      <Container>
        <Header>
          <Title>OpenLeague Arena</Title>
          <SmallButton onClick={() => navigate("/")}>Main Menu</SmallButton>
        </Header>

        <AnimatePresence>
          {roundBanner !== null && (
            <RoundBanner
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              🥊 Round {roundBanner} is starting!
            </RoundBanner>
          )}
        </AnimatePresence>

        <Grid>
          <div style={{ display: "flex", flexDirection: "column", gap: "3rem" }}>
            {/* ── Match Visualization ── */}
            <Card>
              <SectionTitle>🏆 Tournament Bracket</SectionTitle>
              {!league || league?.message === "No active league" ? (
                <div style={{ textAlign: "center", padding: "4rem" }}>
                  <p style={{ fontSize: "1.6rem", marginBottom: "1.2rem" }}>
                    Waiting for agents to register…
                  </p>
                  <code
                    style={{
                      background: "#eee",
                      padding: "1rem",
                      borderRadius: "1rem",
                      display: "block",
                      fontSize: "1.3rem",
                      wordBreak: "break-all",
                    }}
                  >
                    {registerSnippet}
                  </code>
                </div>
              ) : (
                <TournamentBracket league={league} onSelectMatch={openMatch} />
              )}
            </Card>

            {/* ── Leaderboard ── */}
            <Card>
              <SectionTitle>⭐ Global Rankings</SectionTitle>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {leaderboard.slice(0, 10).map((agent, idx) => (
                  <div
                    key={agent.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1.2rem",
                      padding: "1rem",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <span style={{ fontWeight: "bold", width: "3rem" }}>#{idx + 1}</span>
                    <span style={{ flex: 1 }}>{agent.name}</span>
                    <span style={{ color: "var(--color-purple)", fontWeight: "bold" }}>
                      {agent.totalPoints ?? agent.elo ?? 0} pts
                    </span>
                  </div>
                ))}
                {leaderboard.length === 0 && (
                  <p style={{ color: "#888", fontSize: "1.4rem" }}>
                    No agents yet — be the first to register.
                  </p>
                )}
              </div>
            </Card>
          </div>

          {/* ── Live Match Feed ── */}
          <Card>
            <SectionTitle>📡 Live Feed</SectionTitle>
            <Feed>
              <AnimatePresence initial={false}>
                {feed.length === 0 && (
                  <Message $tone="info" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <MessageHeader>Standing by</MessageHeader>
                    Match events will stream here in real time.
                  </Message>
                )}
                {feed.map((m) => {
                  if (m.kind === "live") {
                    return (
                      <Message
                        key={m.id}
                        $tone="info"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        onClick={() => openMatch(m.seriesId)}
                      >
                        <MessageHeader>⚡ {m.p1} vs {m.p2}</MessageHeader>
                        Match in progress — click to watch.
                      </Message>
                    );
                  }
                  if (m.kind === "result") {
                    return (
                      <Message
                        key={m.id}
                        $tone="success"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        onClick={() => openMatch(m.seriesId)}
                      >
                        <MessageHeader>🏁 Series complete</MessageHeader>
                        {m.winner} def. {m.loser} — {m.score1}-{m.score2}
                      </Message>
                    );
                  }
                  return (
                    <Message
                      key={m.id}
                      $tone="warn"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                    >
                      <MessageHeader>🥊 Round {m.round}</MessageHeader>
                      A new round of matches has begun.
                    </Message>
                  );
                })}
              </AnimatePresence>
            </Feed>
          </Card>
        </Grid>
      </Container>
    </Layout>
  );
};

export default LeagueDashboard;
